import {
  InvoiceCategoryFilter,
  OutboundEmailState,
  RutterExportState,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';

import { Settlement } from '~hooks/useSettlements/models';
import { payableInvoiceCategories } from '~pages/Approvals/DriverPay/constants';

interface SettlementCsvRow {
  'Created Date': string;
  'Settlement ID': string;
  'Driver Name': string;
  'For Company': string;
  'Order IDs': string;
  Jobs: string;
  'Quantities/UOMs': string;
  Total: string;
  'Due Date': string;
  State: string;
  Exported: string;
  Notes: string;
  'Commission Deductions'?: string;
}

function getExportedStatus(settlement: Settlement): string {
  const accountingExport = settlement.rutterSettlementExport;
  if (accountingExport) {
    switch (accountingExport.state) {
      case RutterExportState.EXPORTED:
        return `Exported ${accountingExport.exportedAt ? dayjs(accountingExport.exportedAt).format('YYYY-MM-DD') : ''}`;
      case RutterExportState.PENDING:
        return 'Pending';
      case RutterExportState.FAILED:
        return 'Failed';
      case RutterExportState.NOT_EXPORTED:
        break;
      default:
        return '';
    }
  }

  if (settlement.exportedAt) {
    return `Exported ${dayjs(settlement.exportedAt).format('YYYY-MM-DD')}`;
  }

  const outboundEmail = settlement.lastOutboundEmail;
  if (outboundEmail) {
    const date = dayjs(outboundEmail.updatedAt).format('YYYY-MM-DD');
    switch (outboundEmail.state) {
      case OutboundEmailState.SENT:
        return `Delivered ${date}`;
      case OutboundEmailState.RENDERING:
      case OutboundEmailState.SENDING:
        return `Initiated ${date}`;
      case OutboundEmailState.FAILED:
        return `Failed ${date}`;
    }
  }

  return '';
}

// payables show the vendor account, receivables the customer account (driver name has
// its own column, so no fallback to it here)
function getForCompany(settlement: Settlement, category: InvoiceCategoryFilter): string {
  const account = payableInvoiceCategories.includes(category)
    ? settlement.vendorAccount
    : settlement.customerAccount;

  return account?.name ?? '';
}

export function transformSettlementsToCSV(
  settlements: Settlement[],
  category: InvoiceCategoryFilter,
): SettlementCsvRow[] {
  return settlements.map((settlement) => ({
    'Created Date': settlement.createdAt
      ? dayjs(settlement.createdAt).format('YYYY-MM-DD HH:mm:ss')
      : '',
    'Settlement ID': settlement.settlementId || '',
    'Driver Name': settlement.driver?.fullName ?? '',
    'For Company': getForCompany(settlement, category),
    'Order IDs': settlement.data?.friendlyOrderIds?.join(', ') ?? '',
    Jobs: String(settlement.jobsCount ?? 0),
    'Quantities/UOMs': settlement.data?.quantitiesPerUom?.join(', ') ?? '',
    Total: String(settlement.rebillTotalAmount ?? settlement.totalAmount ?? 0),
    'Due Date': settlement.dueDate
      ? dayjs.tz(settlement.dueDate).format('YYYY-MM-DD')
      : '',
    State: settlement.state || '',
    Exported: getExportedStatus(settlement),
    Notes: settlement.notes ?? '',
    ...(payableInvoiceCategories.includes(category) && {
      'Commission Deductions': String(
        settlement.rebillPayCommissionTotalAmount ?? settlement.payCommissionTotalAmount,
      ),
    }),
  }));
}
